import { portalText, usePortalTranslation } from "../../i18n/portal.js";
/**
 * The public design's frame for the portal's sign-in and sign-up pages (ADR 0056 portal amendment, owner decision 2026-10-11).
 *
 * The owner moved LOG IN and SIGN UP to the new wetalk design; the rest of the portal keeps its look. Every page a
 * person meets on the way in — log in, sign up, the second step, the emailed code, finishing the business, an
 * invitation, creating an organization, confirming an emailed link — is drawn inside this frame:
 *
 *   · the public site's header: the lowercase wordmark, Agents · Channels · Platform · Pricing · FAQ · Docs (pages of
 *     wetalk.io, through the configured public-site base), then "Log in" and "Start free" (this portal's routes,
 *     the current one marked by the router's `aria-current="page"`);
 *   · one card on the near-black page, with the moving line field behind it (decoration, still for reduced motion);
 *   · the footer: © wetalk, the language picker (the signed-out bar is not drawn above this frame), the status page.
 *
 * The page's tokens are `public-design-tokens.css`, bound on `.pd-page` only; its layout is `public-frame.css`.
 *
 * AMENDED 2026-10-12 (owner: "on the mobile menu use the hamburger menu"): at phone width the header is the wordmark
 * and a ☰ button; the site links, Log in, Start free and a language picker open as a sheet under it
 * (`phone-menu.ts`). Wider screens are unchanged: the sheet's wrapper is `display: contents` there.
 *
 * AMENDED 2026-10-13 (Claude Design's copy of 20:33 UTC): the header is the public site's header of 13 Oct 2026 —
 * `site-header.tsx`, the Solutions mega menu, the Product and Resources dropdowns, Pricing, Log in, Start free and the
 * flag language menu, and below 64rem a full-screen phone menu — in place of the six site links and the 2026-10-12
 * sheet (`phone-menu.ts`, retired). The card, the line field and the footer (with its language picker) are unchanged.
 *
 * AMENDED 2026-10-14 (Claude Design's copy of 00:05 UTC): behind the card, the public landing page's closing-call line
 * ripple on one canvas (`line-ripple.tsx`) in place of the moving line field, and a radial spotlight in the page's ink
 * behind the card (`.pd-spotlight`, in `main`) so the card sits on a clear centre. No pointer reaction any more.
 *
 * AMENDED 2026-10-15 (design audit against Claude Design's copy of 14 Oct 2026, 23:17 UTC): the footer is the design's —
 * © wetalk and the status page only. Its language picker goes: the header's flag menu is the page's one language control
 * (`useHostedLanguagePicker` still keeps the signed-out bar from drawing a second one).
 */
import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";

import { useGateway } from "../../api/provider.js";
import { authContextQuery } from "../../api/query.js";
import { useHostedLanguagePicker } from "../../i18n/language-control.js";

import { LineRipple } from "./line-ripple.js";
import "./public-design-tokens.css";
import "./public-frame.css";
import { SiteHeader } from "./site-header.js";

export interface PublicFrameProps {
  /** `wide` for the multi-field pages (create an organization, an invitation); the sign-in card is `narrow`. */
  readonly width?: "narrow" | "wide";
  readonly children: ReactNode;
}

/**
 * A page of wetalk.io in the language the portal shows: the public site builds every page again under `/el/` for Greek
 * (ADR 0051's public extension), so a Greek reader lands on the Greek page.
 */
export function usePublicSiteLink(): (path: string) => string {
  const { locale } = usePortalTranslation("core");
  const gateway = useGateway();
  return (path) => gateway.publicSiteUrl(locale === "el" ? `/el${path}` : path);
}

export function PublicFrame({ width = "narrow", children }: PublicFrameProps): ReactNode {
  usePortalTranslation("core");
  useHostedLanguagePicker();
  const gateway = useGateway();
  const site = usePublicSiteLink();
  // The same cache entry the language boundary and the guards read: a signed-in person (creating an organization,
  // opening an invitation) is not offered "Log in" and "Start free".
  const context = useQuery(authContextQuery(gateway)).data;
  const signedIn = context !== undefined && context !== null;

  return (
    <div className="pd-page">
      <LineRipple />
      <a className="pd-skip" href="#pd-main">
        {portalText("core", "public_frame.skip_to_the_form")}
      </a>

      <SiteHeader site={site} signedIn={signedIn} />

      <main id="pd-main" className="pd-main" tabIndex={-1}>
        <div className="pd-spotlight" aria-hidden="true" />
        <div className={width === "wide" ? "pd-card pd-card-wide" : "pd-card"}>{children}</div>
      </main>

      <footer className="pd-foot">
        <span>{portalText("core", "public_frame.copyright", { year: String(new Date().getFullYear()) })}</span>
        <a className="pd-status" href={site("/status/")}>
          <span className="pd-status-dot" aria-hidden="true" />
          {portalText("core", "public_frame.status_page")}
        </a>
      </footer>
    </div>
  );
}

/** The design's "Welcome" pill above a card's title: a gold dot and small capitals. */
export function PublicKicker({ children }: { readonly children: ReactNode }): ReactNode {
  return (
    <span className="pd-kicker">
      <span className="pd-kicker-dot" aria-hidden="true" />
      {children}
    </span>
  );
}

/** Google's mark in its own four colours (the design's), coloured through the token sheet. */
export function GoogleMark(): ReactNode {
  return (
    <svg className="pd-mark pd-mark-google" viewBox="0 0 48 48" aria-hidden="true" focusable="false">
      <path className="pd-google-yellow" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34.1 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
      <path className="pd-google-red" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34.1 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
      <path className="pd-google-green" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
      <path className="pd-google-blue" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
    </svg>
  );
}

/** Microsoft's four squares (the design's). */
export function MicrosoftMark(): ReactNode {
  return (
    <svg className="pd-mark" viewBox="0 0 22 22" aria-hidden="true" focusable="false">
      <rect className="pd-microsoft-red" width="10" height="10" />
      <rect className="pd-microsoft-green" x="12" width="10" height="10" />
      <rect className="pd-microsoft-blue" y="12" width="10" height="10" />
      <rect className="pd-microsoft-yellow" x="12" y="12" width="10" height="10" />
    </svg>
  );
}

/** The envelope inside the email field and the arrow on the call to action. */
export function EnvelopeMark(): ReactNode {
  return (
    <svg className="pd-input-mark" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m3 7 9 6 9-6" />
    </svg>
  );
}

export function ArrowMark(): ReactNode {
  return (
    <svg className="pd-arrow" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  );
}
